import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    password?: string;
    usernamePos?: string;
    employeeNumber?: string;
    socialSecurityNumber?: string;
    startDate?: string;
    isActive: boolean;
    isAllowedToViewCommonReports?: boolean;
    usesTimeStamping?: boolean;
    salaryType?: string;
    role: Role | null;
    addresses: Address[];
    contactInfos: ContactInfo[];
    locations: Location[];
    brands: Brand[];
}

export interface Role {
    id: number;
    roleName: string;
}

export interface AddressType {
    id: number;
    typeName: string;
}

export interface Address {
    id?: number;
    addressType: AddressType;
    addressTypeId?: number;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    [key: string]: unknown;
}

export interface ContactInfoType {
    id: number;
    typeName: string;
    systemTypeName?: string;
}

export interface ContactInfo {
    id?: number;
    contactInfoType: ContactInfoType;
    value?: string;
    [key: string]: unknown;
}

export interface Location {
    id: number;
    locationNumber?: number;
    locationName: string;
}

export interface Brand {
    id: number;
    brandCode: string;
    brandDescription: string;
}

// API calls

export async function getUser(userId: number): Promise<User> {
    return rms2Fetch<User>(`users/${userId}/detail`);
}

export async function createUser(user: Partial<User>): Promise<User> {
    return rms2Fetch<User>('users', {
        method: 'POST',
        body: JSON.stringify({
            firstName: user.firstName,
            lastName: user.lastName,
            username: user.username,
            password: user.password,
            usernamePos: user.usernamePos,
            employeeNumber: user.employeeNumber,
            socialSecurityNumber: user.socialSecurityNumber,
            startDate: user.startDate,
            isActive: user.isActive,
            salaryType: user.salaryType,
            roleId: user.role?.id ?? null,
            locationIds: user.locations?.map(l => l.id) ?? [],
            brandIds: user.brands?.map(b => b.id) ?? [],
            addresses: user.addresses?.map(a => ({
                addressTypeId: a.addressType?.id ?? a.addressTypeId,
                addressLine1: a.addressLine1,
                addressLine2: a.addressLine2,
                addressLine3: a.addressLine3,
                postalCode: a.postalCode,
                city: a.city,
                country: a.country,
            })) ?? [],
            contactInfos: user.contactInfos?.map(c => ({
                contactInfoTypeId: c.contactInfoType?.id,
                value: c.value,
            })) ?? [],
        }),
    });
}

export async function updateUser(userId: number, user: Partial<User>): Promise<User> {
    return rms2Fetch<User>(`users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({
            firstName: user.firstName,
            lastName: user.lastName,
            username: user.username,
            password: user.password,
            usernamePos: user.usernamePos,
            employeeNumber: user.employeeNumber,
            socialSecurityNumber: user.socialSecurityNumber,
            startDate: user.startDate,
            isActive: user.isActive,
            salaryType: user.salaryType,
            roleId: user.role?.id ?? null,
            locationIds: user.locations?.map(l => l.id) ?? [],
            brandIds: user.brands?.map(b => b.id) ?? [],
            addresses: user.addresses?.map(a => ({
                addressTypeId: a.addressType?.id ?? a.addressTypeId,
                addressLine1: a.addressLine1,
                addressLine2: a.addressLine2,
                addressLine3: a.addressLine3,
                postalCode: a.postalCode,
                city: a.city,
                country: a.country,
            })) ?? [],
            contactInfos: user.contactInfos?.map(c => ({
                contactInfoTypeId: c.contactInfoType?.id,
                value: c.value,
            })) ?? [],
        }),
    });
}

export async function getAddressTypes(): Promise<AddressType[]> {
    return rms2Fetch<{ addressTypes: AddressType[] }>('addresstypes')
        .then(r => r.addressTypes);
}

export async function getContactInfoTypes(): Promise<ContactInfoType[]> {
    return rms2Fetch<{ contactInfoTypes: ContactInfoType[] }>('users/contactinfotypes')
        .then(r => r.contactInfoTypes);
}

export async function getValidRoles(): Promise<Role[]> {
    return rms2Fetch<{ roles: Role[] }>('users/validuserroles')
        .then(r => r.roles);
}

export async function getLocations(): Promise<Location[]> {
    return rms2Fetch<{ locations: Location[] }>('users/locations')
        .then(r => r.locations);
}

export async function getBrands(): Promise<Brand[]> {
    return rms2Fetch<{ brands: Brand[] }>('users/brands')
        .then(r => r.brands);
}
